/**
 * Gift With Purchase (GWP) Manager
 * 
 * A reusable, event-driven module that manages gift with purchase functionality.
 * Listens to cart events and automatically adds/removes gift products when conditions are met.
 * 
 * FEATURES:
 * - No UI flickering (atomic cart operations)
 * - Works with existing pub-sub event system
 * - Easy configuration across different stores
 * - Handles sessionStorage for user dismissals (resets on browser session end)
 * - Date range validation
 * - Collection-based threshold filtering
 * - Stock availability checks
 * 
 * USAGE:
 * 1. Add configuration to your liquid file:
 *    <script type="application/json" data-gwp-config>
 *      { "enabled": true, "variantId": 12345, "threshold": 50.00, ... }
 *    </script>
 * 
 * 2. Initialize the manager:
 *    GWPManager.init();
 * 
 * 3. Use processGWP helper in cart flows:
 *    const gwpResult = await processGWP(cartData, 'cart-add');
 */

class GWPManager {
  constructor() {
    this.config = null;
    this.isProcessing = false;
    this.pendingOperations = [];
    this.init();
  }

  /**
   * Initialize the GWP manager
   */
  init() {
    this.config = this.loadConfig();
    
    // if (!this.config || !this.config.enabled) {
    //   console.log('GWP Manager: Disabled or no configuration found');
    //   return;
    // }

    // Listen to custom events for manual cart processing
    document.addEventListener('gwp:process', (e) => {
      this.handleProcessEvent(e);
    });

    console.log('GWP Manager initialized with config:', this.config);

    // Load and process initial cart (for page refreshes, redirects to cart page, etc.)
    this.loadAndProcessInitialCart();
  }

  /**
   * Load configuration from data attribute
   */
  loadConfig() {
    const configElement = document.querySelector('[data-gwp-config]');
    if (!configElement) return null;

    try {
      const config = JSON.parse(configElement.textContent);
      
      // Validate required fields
      if (!config.variantId || !config.threshold) {
        console.log('GWP Config: Missing required fields (variantId, threshold)');
        return null;
      }

      return {
        enabled: config.enabled !== false,
        variantId: config.variantId,
        threshold: config.threshold,
        startDate: config.startDate ? new Date(config.startDate) : null,
        endDate: config.endDate ? new Date(config.endDate) : null,
        localStorageKey: config.localStorageKey || 'gwp-dismissed', // Used as sessionStorage key
        quantity: config.quantity || 1,
        collectionId: config.collectionId || null,
        eligibleMarkets: config.eligibleMarkets || [],
        currentMarket: config.currentMarket || null,
      };
    } catch (e) {
      console.log('GWP Config: Failed to parse configuration', e);
      return null;
    }
  }

  /**
   * Load and process initial cart on page load
   * Handles cases where user is redirected to cart page or page is refreshed
   */
  async loadAndProcessInitialCart() {

    if (!this.config || !this.config.enabled) {
      console.log('GWP Manager: loadAndProcessInitialCart --> Disabled or no configuration found');
      return;
    }

    try {
      const cartElement = document.querySelector('[data-gwp-cart]');
      if (!cartElement) {
        console.log('GWP Manager: No initial cart data found');
        return;
      }

      const cartData = JSON.parse(cartElement.textContent);
      
      // Skip if cart is empty
      if (!cartData.items || cartData.items.length === 0) {
        console.log('GWP Manager: Initial cart is empty, skipping');
        return;
      }

      console.log('GWP Manager: Processing initial cart on page load');
      
      // Process cart with initial data
      const result = await this.processCart(cartData, 'page-load');
      
      console.log('GWP Manager: Initial cart processed', result);
      
      // If gift was added/removed, update UI with section rendering
      if (result.processed && (result.giftAdded || result.giftRemoved)) {
        console.log('GWP Manager: Cart updated on page load, refreshing UI...');
        
        // Use a flag to prevent multiple updates
        if (sessionStorage.getItem('gwp-reload-done')) {
          sessionStorage.removeItem('gwp-reload-done');
          return;
        }
        
        sessionStorage.setItem('gwp-reload-done', 'true');
        
        // Update cart UI based on current page
        await this.updateCartUI();
        
        // Clear reload flag after successful update
        sessionStorage.removeItem('gwp-reload-done');
      }
      
    } catch (e) {
      console.error('GWP Manager: Error loading initial cart', e);
      sessionStorage.removeItem('gwp-reload-done');
    }
  }

  /**
   * Update cart UI using section rendering
   * Detects whether user is on cart page or has cart drawer and updates accordingly
   */
  async updateCartUI() {
    try {
      const routes = window.routes || { cart_url: '/cart' };
      
      // Check if cart drawer exists
      const cartDrawer = document.querySelector('#CartContainerLayoutWp');
      const isCartDrawer = cartDrawer !== null;
      
      // Check if main cart page exists
      const mainCart = document.querySelector('.ContainerWPElementMain');
      const isCartPage = mainCart !== null;
      
      console.log('GWP Manager: Updating cart UI', {
        isCartDrawer,
        isCartPage,
      });
      
      // Update cart drawer if present
      if (isCartDrawer) {
        fetch(`${routes.cart_url}?section_id=cart-drawer`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('#CartContainerLayoutWp');
            if (sourceQty && cartDrawer) {
              cartDrawer.innerHTML = sourceQty.innerHTML;
              console.log('GWP Manager: Cart drawer updated');
            }
          })
          .catch((e) => console.error('GWP Manager: Failed to update cart drawer', e));
      }
      
      // Update main cart page if present
      if (isCartPage) {
        fetch(`${routes.cart_url}?section_id=main-cart-items`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.ContainerWPElementMain');
            if (sourceQty && mainCart) {
              mainCart.innerHTML = sourceQty.innerHTML;
              console.log('GWP Manager: Main cart page updated');
            }
          })
          .catch((e) => console.error('GWP Manager: Failed to update main cart', e));
      }
      
      // Always update cart icon bubble
      fetch(`${routes.cart_url}?section_id=cart-icon-bubble`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('.CartCountMapLayout') || html.querySelector('.cart-count-bubble');
          if (sourceQty) {
            const targets = document.querySelectorAll('.CartCountMapLayout, .cart-count-bubble');
            targets.forEach((evt) => {
              evt.innerHTML = sourceQty.innerHTML;
            });
            console.log('GWP Manager: Cart icon updated');
          }
        })
        .catch((e) => console.error('GWP Manager: Failed to update cart icon', e));
        
    } catch (e) {
      console.error('GWP Manager: Error updating cart UI', e);
    }
  }

  /**
   * Check if GWP conditions are met
   * @param {Object} cartData - Cart data from API response
   * @param {Number} filteredTotal - Filtered total (optionally filtered by collection)
   * @returns {Object} { shouldAdd: boolean, reason: string }
   */
  checkConditions(cartData, filteredTotal = null) {
    // debugger;
    if (!this.config) {
      return { shouldAdd: false, reason: 'Not configured' };
    }

    // Check if dismissed by user
    if (this.isDismissed()) {
      return { shouldAdd: false, reason: 'User dismissed' };
    }

    // Check market eligibility
    if (!this.isMarketEligible()) {
      return { shouldAdd: false, reason: 'Market not eligible' };
    }

    // Check date range
    if (!this.isWithinDateRange()) {
      return { shouldAdd: false, reason: 'Outside date range' };
    }

    // Check if gift is already in cart
    if (this.isGiftInCart(cartData)) {
      return { shouldAdd: false, reason: 'Gift already in cart' };
    }

    // Check threshold using filtered total if provided
    const subtotal = filteredTotal !== null 
      ? filteredTotal 
      : (cartData.items_subtotal_price || cartData.total_price || 0);
      
    if (subtotal < this.config.threshold * 100) { // Convert to cents
      return { shouldAdd: false, reason: 'Below threshold' };
    }

    return { shouldAdd: true, reason: 'Conditions met' };
  }

  /**
   * Check if current date is within GWP date range
   * 
   * NOTE: Currently uses Canada/Toronto timezone (America/Toronto - Eastern Time)
   * regardless of user's location for consistent GWP activation times.
   * 
   * TODO: Make timezone configurable per market or use user's local timezone
   * based on business requirements. Consider adding timezone to config:
   * - config.timezone = 'America/Toronto' (default)
   * - config.timezone = 'America/Vancouver' (Pacific)
   * - Or use localization.market.timezone when available
   */
  isWithinDateRange() {
    // Get current time in Canada/Toronto timezone
    // This ensures GWP activates at the same wall-clock time for all users
    const nowInCanada = new Date(new Date().toLocaleString('en-US', { 
      timeZone: 'America/Toronto' 
    }));

    if (this.config.startDate && nowInCanada < this.config.startDate) {
      console.log('GWP Manager: Before start date', {
        nowInCanada: nowInCanada.toISOString(),
        startDate: this.config.startDate.toISOString(),
      });
      return false;
    }

    if (this.config.endDate && nowInCanada > this.config.endDate) {
      console.log('GWP Manager: After end date', {
        nowInCanada: nowInCanada.toISOString(),
        endDate: this.config.endDate.toISOString(),
      });
      return false;
    }

    return true;
  }

  /**
   * Check if current market is eligible for GWP
   * @returns {boolean}
   */
  isMarketEligible() {
    // If no eligible markets specified, all markets are eligible
    if (!this.config.eligibleMarkets || this.config.eligibleMarkets.length === 0) {
      return true;
    }

    // If no current market, cannot determine eligibility (fallback: allow)
    if (!this.config.currentMarket) {
      console.warn('GWP Manager: No current market specified, allowing GWP');
      return true;
    }

    // Check if current market is in eligible markets list
    const isEligible = this.config.eligibleMarkets.includes(this.config.currentMarket);
    
    if (!isEligible) {
      console.log('GWP Manager: Current market not eligible', {
        currentMarket: this.config.currentMarket,
        eligibleMarkets: this.config.eligibleMarkets,
      });
    }

    return isEligible;
  }

  /**
   * Check if gift is already in cart
   */
  isGiftInCart(cartData) {
    if (!cartData.items || !Array.isArray(cartData.items)) {
      return false;
    }

    return cartData.items.some(item => 
      item.variant_id === this.config.variantId || 
      item.id === this.config.variantId
    );
  }

  /**
   * Check if GWP was dismissed by user
   * Uses sessionStorage - dismissal persists only for current browser session
   * 
   * TODO: Consider better UX options:
   * - Reset dismissed when threshold drops below minimum
   * - Time-based dismissal (e.g., 24 hours)
   * - Permanent dismissal with localStorage option
   */
  isDismissed() {
    try {
      const dismissed = sessionStorage.getItem(this.config.localStorageKey);
      return dismissed === 'true';
    } catch (e) {
      console.warn('GWP Manager: sessionStorage access failed', e);
      return false;
    }
  }

  /**
   * Mark GWP as dismissed
   * Uses sessionStorage - resets on browser session end (tab close, browser restart)
   * 
   * TODO: Consider UX improvements:
   * - Auto-reset dismissed when user's cart total significantly increases
   * - Different dismissal strategies per use case
   * - Analytics tracking for dismissed events
   */
  setDismissed(isDismissed = true) {
    try {
      sessionStorage.setItem(this.config.localStorageKey, isDismissed ? 'true' : 'false');
      console.log('GWP Manager: Dismissal state updated (session only)');
    } catch (e) {
      console.warn('GWP Manager: sessionStorage write failed', e);
    }
  }

  /**
   * Handle custom gwp:process event
   * This is the main entry point for manual cart processing
   */
  async handleProcessEvent(event) {
    const { cartData, source, callback } = event.detail;

    if (!callback || typeof callback !== 'function') {
      console.error('GWP Manager: No callback provided in event');
      return;
    }

    if (!this.config || !this.config.enabled) {
      console.log('GWP Manager: Not configured or disabled');
      callback({
        success: true,
        processed: false,
        reason: 'GWP not configured or disabled',
        giftAdded: false,
        giftRemoved: false,
        cartData: cartData,
      });
      return;
    }

    console.log('GWP Manager: Processing cart from source:', source);

    try {
      const result = await this.processCart(cartData, source);
      callback(result);
    } catch (error) {
      console.error('GWP Manager: Error processing cart', error);
      callback({
        success: false,
        error: error.message,
        giftAdded: false,
        giftRemoved: false,
        cartData: cartData,
      });
    }
  }

  /**
   * Process cart data and add/remove gift if needed
   * @param {Object} cartData - Cart data from API response (ignored, we fetch fresh data)
   * @param {String} source - Source of the cart update (cart-add, cart-update, etc)
   * @returns {Promise<Object>} Result object with details
   */
  async processCart(cartData, source = 'unknown') {
    try {
      // Fetch fresh cart data with collection information
      const fullCartData = await this.fetchCartWithCollections();
      
      // Calculate filtered total (respects collection filter if configured)
      const filteredTotal = this.calculateFilteredTotal(fullCartData);
      
      // Check conditions with filtered total
      const { shouldAdd, reason } = this.checkConditions(fullCartData, filteredTotal);

      console.log('GWP Manager: Condition check result -', {
        shouldAdd,
        reason,
        source,
        filteredTotal: filteredTotal,
        threshold: this.config.threshold * 100,
        collectionFilter: this.config.collectionId ? 'enabled' : 'disabled',
      });

      // Should add gift
      if (shouldAdd) {
        const addResult = await this.addGiftToCartSync();
        return {
          success: true,
          processed: true,
          giftAdded: addResult.success,
          giftRemoved: false,
          reason: addResult.success ? 'Gift added successfully' : addResult.error,
          error: addResult.error || null,
          updatedCartData: addResult.cartData || null,
          originalCartData: fullCartData,
          filteredTotal: filteredTotal,
        };
      }

      // Check if gift should be removed (below threshold)
      const isGiftPresent = this.isGiftInCart(fullCartData);
      const belowThreshold = filteredTotal < this.config.threshold * 100;

      if (isGiftPresent && belowThreshold && !this.isDismissed()) {
        const removeResult = await this.removeGiftFromCartSync();
        return {
          success: true,
          processed: true,
          giftAdded: false,
          giftRemoved: removeResult.success,
          reason: removeResult.success ? 'Gift removed (below threshold)' : removeResult.error,
          error: removeResult.error || null,
          updatedCartData: removeResult.cartData || null,
          originalCartData: fullCartData,
          filteredTotal: filteredTotal,
        };
      }

      // No action needed
      return {
        success: true,
        processed: false,
        giftAdded: false,
        giftRemoved: false,
        reason: reason,
        error: null,
        updatedCartData: null,
        originalCartData: fullCartData,
        filteredTotal: filteredTotal,
      };
    } catch (error) {
      console.error('GWP Manager: Error in processCart', error);
      return {
        success: false,
        processed: false,
        giftAdded: false,
        giftRemoved: false,
        reason: 'Failed to fetch cart data',
        error: error.message,
        updatedCartData: null,
        originalCartData: cartData,
        filteredTotal: 0,
      };
    }
  }

  /**
   * Handle cart error
   */
  handleCartError(payload) {
    console.log('GWP Manager: Cart error detected', payload);
    this.isProcessing = false;
  }

  /**
   * Fetch cart data with collection information
   * Uses Section Rendering API to get cart items with their collections
   * @returns {Promise<Object>} Cart data with collection IDs for each item
   */
  async fetchCartWithCollections() {
    try {
      console.log('GWP Manager: Fetching cart with collections...');
      
      const response = await fetch('/cart?section_id=gwp-cart-data');
      const html = await response.text();
      
      // Parse HTML to get the data element
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const dataElement = doc.querySelector('[data-gwp-cart]');
      
      if (!dataElement) {
        throw new Error('Cart data element not found in section rendering');
      }
      
      const cartData = JSON.parse(dataElement.textContent);
      console.log('GWP Manager: Cart with collections fetched successfully', {
        item_count: cartData.item_count,
        total_price: cartData.total_price,
      });
      
      return cartData;
    } catch (error) {
      console.error('GWP Manager: Failed to fetch cart with collections', error);
      throw error;
    }
  }

  /**
   * Calculate filtered total based on collection filter
   * @param {Object} cartData - Cart data with collection information
   * @returns {Number} Filtered total in cents
   */
  calculateFilteredTotal(cartData) {
    // No collection filter - use regular total
    if (!this.config.collectionId) {
      const total = cartData.items_subtotal_price || cartData.total_price || 0;
      console.log('GWP Manager: No collection filter, using total:', total);
      return total;
    }

    // Filter items by collection
    let filteredTotal = 0;
    let matchedCount = 0;

    for (const item of cartData.items) {
      // Check if item is in the target collection
      if (item.collections && item.collections.includes(this.config.collectionId)) {
        filteredTotal += item.line_price;
        matchedCount++;
        console.log(`GWP Manager: Item "${item.title}" matches collection, line_price: ${item.line_price}`);
      }
    }

    console.log('GWP Manager: Collection filter applied', {
      collectionId: this.config.collectionId,
      totalItems: cartData.items.length,
      matchedItems: matchedCount,
      filteredTotal: filteredTotal,
    });

    return filteredTotal;
  }

  /**
   * Add gift product to cart (synchronous version that returns result)
   * Used by processCart method
   */
  async addGiftToCartSync() {
    if (this.isProcessing) {
      console.log('GWP Manager: Already processing, waiting...');
      // Wait a bit and retry
      await new Promise(resolve => setTimeout(resolve, 100));
      if (this.isProcessing) {
        return { success: false, error: 'Another operation in progress', cartData: null };
      }
    }

    this.isProcessing = true;

    try {
      console.log('GWP Manager: Adding gift to cart...');
      
      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              id: this.config.variantId,
              quantity: this.config.quantity,
              properties: {
                '_gwp': 'true', // Private property to mark as GWP
              },
            },
          ],
        }),
      });

      const result = await response.json();

      if (result.status) {
        // Error response from Shopify
        console.error('GWP Manager: Failed to add gift', result);
        return {
          success: false,
          error: result.description || result.message,
          cartData: null,
        };
      } else {
        console.log('GWP Manager: Gift added successfully');
        return {
          success: true,
          error: null,
          cartData: result,
        };
      }
    } catch (error) {
      console.error('GWP Manager: Error adding gift', error);
      return {
        success: false,
        error: error.message,
        cartData: null,
      };
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Remove gift product from cart (synchronous version that returns result)
   * Used by processCart method
   */
  async removeGiftFromCartSync() {
    if (this.isProcessing) {
      console.log('GWP Manager: Already processing, waiting...');
      await new Promise(resolve => setTimeout(resolve, 100));
      if (this.isProcessing) {
        return { success: false, error: 'Another operation in progress', cartData: null };
      }
    }

    this.isProcessing = true;

    try {
      console.log('GWP Manager: Removing gift from cart...');
      
      // Get current cart to find the gift item line
      const cartResponse = await fetch(window.Shopify.routes.root + 'cart.js');
      const cart = await cartResponse.json();

      const giftItem = cart.items.find(item => 
        item.variant_id === this.config.variantId || item.id === this.config.variantId
      );

      if (!giftItem) {
        console.log('GWP Manager: Gift not found in cart');
        return {
          success: true,
          error: null,
          cartData: cart,
        };
      }

      // Use line number (1-based) to remove
      const lineNumber = cart.items.indexOf(giftItem) + 1;

      const response = await fetch(window.Shopify.routes.root + 'cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          line: lineNumber,
          quantity: 0,
        }),
      });

      const result = await response.json();

      console.log('GWP Manager: Gift removed successfully');
      return {
        success: true,
        error: null,
        cartData: result,
      };
    } catch (error) {
      console.error('GWP Manager: Error removing gift', error);
      return {
        success: false,
        error: error.message,
        cartData: null,
      };
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Publish GWP event
   */
  publish(eventType, payload) {
    if (typeof publish === 'function') {
      const eventName = `gwp:${eventType}`;
      publish(eventName, payload);
    }

    // Also dispatch custom event for other listeners
    const event = new CustomEvent('gwpStatusChange', {
      detail: { type: eventType, ...payload },
    });
    document.dispatchEvent(event);
  }

  /**
   * Reset GWP state (useful for testing or manual reset)
   * Clears dismissed state from sessionStorage
   */
  reset() {
    this.isProcessing = false;
    this.pendingOperations = [];
    this.setDismissed(false);
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.gwpManager = new GWPManager();
  });
} else {
  window.gwpManager = new GWPManager();
}

/**
 * Helper function to process GWP via custom events
 * This is the recommended way to integrate GWP into your cart flows
 * 
 * @param {Object} cartData - Cart data from Shopify API response
 * @param {String} source - Source identifier (e.g., 'cart-add', 'cart-update', 'cart-change')
 * @returns {Promise<Object>} Result object with GWP processing details
 * 
 * @example
 * // In your cart add flow:
 * .then(async (response) => {
 *   const cartData = await response.json();
 *   const gwpResult = await processGWP(cartData, 'cart-add');
 *   console.log('GWP Result:', gwpResult);
 *   return response.text(); // Continue with section rendering
 * })
 */
window.processGWP = async function(cartData, source = 'unknown') {



  return new Promise((resolve) => {
    // Dispatch event with callback
    document.dispatchEvent(new CustomEvent('gwp:process', {
      detail: {
        cartData: cartData,
        source: source,
        callback: resolve, // GWP Manager will call this with result
      },
    }));
  });
};
